<template>
  <b-modal
    v-model="localOpen"
    size="md"
    centered
    hide-footer
    title="Update Profile Picture"
    @hidden="resetAll"
  >
    <b-form @submit.prevent="onClickReverse">
      <div class="signature-container">
        <div
          class="avatar-wrapper"
          @click="openFile"
        >
          <b-avatar
            size="120"
            :src="preview || photo || undefined"
          />
          <div
            v-if="!uploading"
            class="edit-overlay"
          >
            <Edit2Icon />
          </div>
        </div>
        <b-form-file
          ref="fileInput"
          class="d-none"
          accept="image/*"
          @change="onFileChange"
        />
        <div
          v-if="file && !uploaded"
          class="upload-box mt-3 position-relative p-2"
        >
          <!-- Close icon -->
          <span
            v-b-tooltip.hover
            class="close-icon"
            title="Remove file"
            @click="removeFile"
          >
            &times;
          </span>

          <!-- Row: Avatar + File info (left), Upload status (right) -->
          <div class="d-flex align-items-center justify-content-between">
            <!-- Left: Avatar + filename/filesize -->
            <div class="d-flex align-items-center">
              <b-avatar
                size="36"
                variant="light-primary"
                :src="preview || photo || undefined"
              />

              <div class="ml-2">
                <div
                  v-b-tooltip.hover
                  class="file-name font-weight-bold"
                  :title="file.name"
                >
                  {{ file.name.length > 15 ? file.name.slice(0, 15) + '...' : file.name }}
                </div>

                <div
                  class="file-status text-muted"
                  style="font-size: 12px;"
                >
                  {{ fileSizeText }}
                </div>
              </div>
            </div>

            <div
              class="mt-1"
            >
              Uploading
            </div>
          </div>
          <b-progress
            height="6px"
            :value="progress"
            max="100"
            class="mt-2"
            variant="primary"
            animated
            striped
          />
        </div>

      </div>
      <div
        v-if="!uploading"
        class="d-flex justify-content-end mt-3"
      >
        <b-button
          v-ripple.400="'rgba(186, 191, 199, 0.15)'"
          type="button"
          class="mr-1"
          variant="outline-secondary"
          :disabled="loading"
          @click="cancelUpload"
        >
          Cancel
        </b-button>

        <b-button
          class="custom-button-color"
          type="submit"
          :disabled="loading || !form.profile_logo"
        >
          Update
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import {
  BAvatar, BFormFile, BProgress, BButton, BForm,
} from 'bootstrap-vue'
import { Edit2Icon } from 'vue-feather-icons'
import Ripple from 'vue-ripple-directive'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'

const ProfileRepository = RepositoryFactory.get('profile')

export default {
  directives: {
    ripple: Ripple,
  },

  components: {
    BAvatar,
    BFormFile,
    BProgress,
    Edit2Icon,
    BButton,
    BForm,
  },
  mixins: [SuccessMessage, ErrorMessage],

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    photo: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      form: {
        profile_logo: null,
      },
      file: null,
      preview: null,
      progress: 0,
      uploaded: false,
      loading: false,

    }
  },

  computed: {
    fileSizeText() {
      if (!this.file) return ''
      const total = Math.round(this.file.size / 1024)
      const current = Math.round((total * this.progress) / 100)
      return `${current} KB of ${total} KB`
    },

    localOpen: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      },
    },
  },

  methods: {
    openFile() {
      this.$refs.fileInput.$el.querySelector('input').click()
    },

    onFileChange(event) {
      const file = event.target.files[0]
      if (!file) return

      this.form.profile_logo = file
      this.file = file
      this.preview = URL.createObjectURL(file)

      this.progress = 0
      this.uploaded = false
      this.uploading = true

      this.uploadFile()
    },

    removeFile() {
      this.form.profile_logo = null
      this.file = null
      this.preview = null
      this.progress = 0
      this.uploaded = false
      this.uploading = false

      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset()
      }
    },
    cancelUpload() {
      this.localOpen = false
      this.resetAll()
    },
    resetAll() {
      this.form.profile_logo = null
      this.file = null
      this.preview = null
      this.progress = 0
      this.uploaded = false
      this.uploading = false
      this.loading = false
      if (this.$refs.fileInput) {
        this.$refs.fileInput.reset()
      }
    },
    uploadFile() {
      const interval = setInterval(() => {
        if (this.progress >= 100) {
          clearInterval(interval)
          this.uploading = false
          this.uploaded = true
          this.$emit('uploaded', this.file)

          setTimeout(() => {
            this.file = null
            this.progress = 0
          }, 1500)
        } else {
          this.progress += 10
        }
      }, 300)
    },
    async onClickReverse() {
      this.loading = true
      try {
        if (!this.form.profile_logo) {
          this.showErrorMessage('Please select a profile image.')
          return
        }

        const res = await ProfileRepository.updateprofileImage(
          this.form.profile_logo,
        )

        if (res.status === 200) {
          this.showSuccessMessage('Profile Updated Successfully')
          localStorage.removeItem('businessSettings')
          this.localOpen = false
        } else {
          this.showErrorMessage('Cannot Update Profile')
        }
      } catch (e) {
        this.showErrorMessage(e)
      } finally {
        this.loading = false
      }
    },
  },

}
</script>

<style scoped>
.signature-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.avatar-wrapper {
  position: relative;
  width: 120px;
  height: 120px;
  cursor: pointer;
}

.avatar-wrapper img {
  object-fit: cover;
}

.edit-overlay {
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 32px;
  height: 32px;
  background: #0d6efd;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,.25);
}

.edit-overlay svg {
  width: 14px;
  height: 14px;
  stroke: #fff;
}

.upload-box {
  position: relative;
  width: 100%;
  max-width: 420px;
  padding: 12px;
  border-radius: 10px;
  background: #f8f9fa;
  box-shadow: 0 1px 3px rgba(0,0,0,.08);
}

.close-icon {
  position: absolute;
  top: 6px;
  right: 8px;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;
  color: #6c757d;
}

.close-icon:hover {
  color: #dc3545;
}

.file-name {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-status {
  font-size: 12px;
  color: #6c757d;
}

.file-percent {
  font-size: 12px;
  font-weight: 600;
}

</style>
